import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Edit2, Trash2, Loader2, AlertCircle, ListTree } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = '/api/contratos/';

export default function ContratosPage() {
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({ nombre: '', activo: '' });
  const [showModal, setShowModal] = useState(false);
  const [editingContrato, setEditingContrato] = useState(null);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { data, isLoading, error } = useQuery({
    queryKey: ['contratos', page, filters],
    queryFn: async () => {
      const params = { page, page_size: 50 };
      if (filters.nombre) params.nombre = filters.nombre;
      if (filters.activo) params.activo = filters.activo;
      const response = await axios.get(API_URL, { params });
      return response.data;
    },
  });

  const createMutation = useMutation({
    mutationFn: async (data) => {
      const response = await axios.post(API_URL, data);
      return response.data;
    },
    onSuccess: () => {
      toast.success('Contrato creado exitosamente');
      queryClient.invalidateQueries(['contratos']);
      setShowModal(false);
      setEditingContrato(null);
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Error creando contrato');
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }) => {
      const response = await axios.put(`${API_URL}${id}`, data);
      return response.data;
    },
    onSuccess: () => {
      toast.success('Contrato actualizado exitosamente');
      queryClient.invalidateQueries(['contratos']);
      setShowModal(false);
      setEditingContrato(null);
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Error actualizando contrato');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id) => {
      const response = await axios.delete(`${API_URL}${id}`);
      return response.data;
    },
    onSuccess: () => {
      toast.success('Contrato eliminado exitosamente');
      queryClient.invalidateQueries(['contratos']);
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Error eliminando contrato');
    },
  });

  const handleOpenModal = (contrato = null) => {
    setEditingContrato(contrato);
    setShowModal(true);
  };

  const handleDelete = (id) => {
    if (window.confirm('¿Está seguro de eliminar este contrato? Se desactivarán también sus condiciones.')) {
      deleteMutation.mutate(id);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Contratos de Agencias</h1>
          <p className="mt-2 text-gray-600">
            Contratos base que determinan el tipo de liquidación según la tarifa.
          </p>
        </div>
        <button
          onClick={() => handleOpenModal()}
          className="btn-primary flex items-center space-x-2"
        >
          <Plus className="w-5 h-5" />
          <span>Nuevo Contrato</span>
        </button>
      </div>

      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Filtros</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Nombre del Contrato</label>
            <input
              type="text"
              value={filters.nombre}
              onChange={(e) => { setFilters({ ...filters, nombre: e.target.value }); setPage(1); }}
              placeholder="Buscar por nombre..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Estado</label>
            <select
              value={filters.activo}
              onChange={(e) => { setFilters({ ...filters, activo: e.target.value }); setPage(1); }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
            >
              <option value="">Todos</option>
              <option value="true">Activos</option>
              <option value="false">Inactivos</option>
            </select>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-6">Contratos Registrados</h2>

        {isLoading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 text-primary animate-spin" />
          </div>
        )}

        {error && (
          <div className="flex items-center space-x-2 p-4 bg-red-50 rounded-lg text-red-700">
            <AlertCircle className="w-5 h-5" />
            <span>Error cargando contratos</span>
          </div>
        )}

        {data && !isLoading && (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nombre</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Condiciones</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Estado</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Acciones</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {data.data.map((contrato) => (
                    <tr key={contrato.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{contrato.nombre}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {contrato.total_condiciones || 0} condición(es)
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${contrato.activo ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                          {contrato.activo ? 'Activo' : 'Inactivo'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-3">
                          <button
                            onClick={() => navigate(`/contratos/${contrato.id}/condiciones`)}
                            className="text-green-600 hover:text-green-900 flex items-center gap-1"
                            title="Gestionar condiciones"
                          >
                            <ListTree className="w-4 h-4" />
                            <span className="hidden sm:inline">Condiciones</span>
                          </button>
                          <button
                            onClick={() => handleOpenModal(contrato)}
                            className="text-blue-600 hover:text-blue-900"
                            title="Editar"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(contrato.id)}
                            className="text-red-600 hover:text-red-900"
                            title="Eliminar"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex items-center justify-between mt-6">
              <div className="text-sm text-gray-700">
                Mostrando <span className="font-medium">{((page - 1) * data.page_size) + 1}</span> a{' '}
                <span className="font-medium">{Math.min(page * data.page_size, data.total)}</span> de{' '}
                <span className="font-medium">{data.total}</span> contratos
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >Anterior</button>
                <button
                  onClick={() => setPage(p => p + 1)}
                  disabled={page >= data.total_pages}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >Siguiente</button>
              </div>
            </div>
          </>
        )}
      </div>

      {showModal && (
        <ContratoModal
          contrato={editingContrato}
          onClose={() => { setShowModal(false); setEditingContrato(null); }}
          onSubmit={(formData) => {
            if (editingContrato) {
              updateMutation.mutate({ id: editingContrato.id, data: formData });
            } else {
              createMutation.mutate(formData);
            }
          }}
          isLoading={createMutation.isPending || updateMutation.isPending}
        />
      )}
    </div>
  );
}

function ContratoModal({ contrato, onClose, onSubmit, isLoading }) {
  const [formData, setFormData] = useState({
    nombre: contrato?.nombre || '',
    activo: contrato?.activo ?? true
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.nombre.trim()) {
      toast.error('El nombre es obligatorio');
      return;
    }
    onSubmit(formData);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        <div className="px-6 py-4 border-b flex justify-between items-center">
          <h3 className="text-lg font-semibold">
            {contrato ? 'Editar Contrato' : 'Nuevo Contrato'}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">&times;</button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Nombre *</label>
            <input
              type="text"
              value={formData.nombre}
              onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              placeholder="Ej: Contrato Canal Norte"
              required
            />
            <p className="text-xs text-gray-500 mt-1">
              Este nombre debe coincidir con el valor de <em>esquema_liquidacion</em> de la tarifa.
            </p>
          </div>
          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="activo"
              checked={formData.activo}
              onChange={(e) => setFormData({ ...formData, activo: e.target.checked })}
              className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
            />
            <label htmlFor="activo" className="text-sm font-medium text-gray-700">Activo</label>
          </div>
          <div className="flex justify-end space-x-3 pt-4">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50">
              Cancelar
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-4 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary-dark disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? 'Guardando...' : 'Guardar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
